import React, { useState, useEffect } from 'react';
import {
  X, 
  DollarSign, 
  TrendingUp, 
  TrendingDown, 
  Lock,
  AlertCircle,
  CheckCircle,
  Eye,
  EyeOff
} from 'lucide-react';
import { apiFetch } from "../utils/apiClient";

const CashMovementModal = ({ 
  isOpen, 
  onClose, 
  currentUser, 
  currentSession,
  onMovementCreated,
  darkAmountInput = false,
}) => {
  const [formData, setFormData] = useState({
    type: 'deposit',
    amount: '',
    reason: '',
    pinCode: ''
  });
  
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(0);
  const formatAmount = (value) =>
    new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Math.max(0, Number(value) || 0));

  // Montants suggérés
  const suggestedAmounts = [
    { value: 5000, label: '5,000' },
    { value: 10000, label: '10,000' },
    { value: 20000, label: '20,000' },
    { value: 50000, label: '50,000' },
    { value: 100000, label: '100,000' },
    { value: 250000, label: '250,000' }
  ];

  useEffect(() => {
    if (isOpen && currentSession?.id) {
      loadCurrentBalance();
      resetForm();
    }
  }, [isOpen, currentSession]);

  const loadCurrentBalance = async () => {
    try {
      const response = await apiFetch(
        `/api/cash-movements/balance/${currentSession.id}`,
        { cache: "no-store" },
      );
      if (response.ok) {
        const data = await response.json();
        setCurrentBalance(data.balance);
      }
    } catch (error) {
      console.error('❌ Erreur chargement solde:', error);
    }
  };

  const resetForm = () => {
    setFormData({
      type: 'deposit',
      amount: '',
      reason: '',
      pinCode: ''
    });
    setErrors({});
    setShowPin(false);
  };

  const validateForm = () => {
    const newErrors = {};

    // Validation du montant
    const amount = parseFloat(formData.amount);
    if (!formData.amount || isNaN(amount) || amount <= 0) {
      newErrors.amount = 'Montant invalide (doit être positif)';
    }

    // Validation spéciale pour les retraits
    if (formData.type === 'withdrawal' && amount > currentBalance) {
      newErrors.amount = 'Montant supérieur au solde disponible';
    }

    // Validation du PIN
    if (!formData.pinCode || formData.pinCode.length < 4) {
      newErrors.pinCode = 'Code PIN requis (minimum 4 chiffres)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      const response = await apiFetch("/api/cash-movements", {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          sessionId: currentSession.id,
          type: formData.type,
          amount: parseFloat(formData.amount),
          reason: formData.reason || null,
          pinCode: formData.pinCode
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Succès
        if (onMovementCreated) {
          onMovementCreated(data.movement);
        }
        
        // Animation de succès
        setErrors({});
        
        // Fermer le modal après un court délai
        setTimeout(() => {
          onClose();
          resetForm();
        }, 1000);

      } else {
        // Erreur serveur
        setErrors({
          submit: data.error || 'Erreur lors de l\'enregistrement'
        });
      }

    } catch (error) {
      console.error('❌ Erreur mouvement caisse:', error);
      setErrors({
        submit: 'Erreur de connexion au serveur'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAmountSuggestion = (amount) => {
    setFormData(prev => ({
      ...prev,
      amount: amount.toString()
    }));
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 backdrop-blur-md px-4 py-6">
      <div className="w-full max-w-4xl overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-gray-900 to-gray-950 shadow-2xl relative">
        <div className="flex items-start justify-between bg-gradient-to-r from-indigo-500 via-blue-600 to-emerald-500 px-8 py-6 text-white">
          <div>
            <p className="text-xs uppercase tracking-[0.4em] text-white/70">Flux de trésorerie</p>
            <h2 className="mt-1 text-3xl font-semibold">Mouvement de caisse intermédiaire</h2>
            <p className="mt-2 text-sm text-white/80">Sécurisez vos dépôts et retraits au cours de la session.</p>
          </div>
          <button
            onClick={onClose}
            className="rounded-full bg-white/20 p-2 text-white transition hover:bg-white/30"
          >
            <X size={20} />
          </button>
        </div>

        <div className="grid gap-4 border-b border-white/10 bg-gray-900/40 px-8 py-6 text-white sm:grid-cols-3">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-sm text-white/60">Solde disponible</p>
            <div className="mt-2 flex items-center gap-2 text-2xl font-semibold">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              {formatAmount(currentBalance)} FCFA
            </div>
            <p className="text-xs text-white/50">
              Session #{currentSession?.id ?? '—'}
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-sm text-white/60">Type sélectionné</p>
            <div className="mt-2 flex items-center gap-2 text-lg font-semibold">
              {formData.type === 'deposit' ? (
                <>
                  <TrendingUp className="h-5 w-5 text-emerald-400" />
                  Dépôt
                </>
              ) : (
                <>
                  <TrendingDown className="h-5 w-5 text-rose-400" />
                  Retrait
                </>
              )}
            </div>
            <p className="text-xs text-white/50">
              Ajustez la trésorerie pendant le service
            </p>
          </div>

          <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
            <p className="text-sm text-white/60">Caissier</p>
            <p className="mt-1 text-lg font-semibold">
              {currentUser?.first_name} {currentUser?.last_name}
            </p>
            <p className="text-xs text-white/50 uppercase tracking-wide">
              {currentUser?.role}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="grid gap-6 px-8 py-8 text-white lg:grid-cols-2">
          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <p className="text-sm font-medium text-white/70">Choisir l'opération</p>
              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'deposit' }))}
                  className={`flex flex-col items-center rounded-2xl border p-4 text-center transition ${formData.type === 'deposit' ? 'border-emerald-400 bg-emerald-400/10 text-emerald-100 shadow-lg shadow-emerald-500/20' : 'border-white/10 hover:border-emerald-400/50'}`}
                >
                  <TrendingUp size={26} />
                  <span className="mt-2 text-sm font-semibold">Dépôt</span>
                  <span className="text-xs text-white/60">Alimentation du tiroir</span>
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, type: 'withdrawal' }))}
                  className={`flex flex-col items-center rounded-2xl border p-4 text-center transition ${formData.type === 'withdrawal' ? 'border-rose-400 bg-rose-400/10 text-rose-100 shadow-lg shadow-rose-500/20' : 'border-white/10 hover:border-rose-400/50'}`}
                >
                  <TrendingDown size={26} />
                  <span className="mt-2 text-sm font-semibold">Retrait</span>
                  <span className="text-xs text-white/60">Versement ou sécurisation</span>
                </button>
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <label className="text-sm font-medium text-white/70">Montant (FCFA) *</label>
              <div className="mt-3 rounded-2xl border border-white/10 bg-gray-900/70 px-4 py-3">
                <div className="flex items-center gap-2 text-3xl font-semibold">
                  <DollarSign className="h-5 w-5 text-indigo-300" />
                  {formData.amount ? formatAmount(formData.amount) : '0'} FCFA
                </div>
                <input
                  type="number"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className={`mt-3 w-full rounded-xl border px-4 py-3 text-white outline-none transition placeholder:text-white/30 focus:border-indigo-400 appearance-none ${
                    darkAmountInput ? 'border-white/5 bg-gray-950/80' : 'border-white/5 bg-gray-950/60'
                  }`}
                  style={darkAmountInput ? { backgroundColor: 'rgba(5,6,10,0.9)' } : undefined}
                  placeholder="Saisir ou utiliser un montant rapide"
                  min="0"
                  step="1"
                />
              </div>
              {errors.amount && (
                <p className="mt-2 flex items-center text-sm text-rose-400">
                  <AlertCircle size={15} className="mr-2" />
                  {errors.amount}
                </p>
              )}
              <div className="mt-4 flex flex-wrap gap-2">
                {suggestedAmounts.map((suggestion) => (
                  <button
                    key={suggestion.value}
                    type="button"
                    onClick={() => handleAmountSuggestion(suggestion.value)}
                    className="rounded-full border border-white/10 px-3 py-1 text-sm text-white/80 transition hover:border-indigo-400 hover:bg-indigo-400/10"
                  >
                    {suggestion.label} FCFA
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <label className="text-sm font-medium text-white/70">Motif (optionnel)</label>
              <textarea
                value={formData.reason}
                onChange={(e) => setFormData(prev => ({ ...prev, reason: e.target.value }))}
                rows="4"
                placeholder="Ex: Versement en banque, renfort de monnaie…"
                className="mt-3 w-full rounded-xl border border-white/5 bg-gray-950/60 px-4 py-3 text-white outline-none transition placeholder:text-white/30 focus:border-indigo-400"
              />
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
              <label className="text-sm font-medium text-white/70">Votre code PIN *</label>
              <p className="mt-1 text-xs text-white/50">
                Le mouvement sera enregistré au nom de {currentUser?.first_name || 'l’utilisateur connecté'}.
              </p>
              <div className="relative mt-3">
                <input
                  type={showPin ? 'text' : 'password'}
                  value={formData.pinCode}
                  onChange={(e) => setFormData(prev => ({ ...prev, pinCode: e.target.value }))}
                  maxLength={6}
                  placeholder="Saisissez votre code confidentiel"
                  className={`w-full rounded-xl border px-4 py-3 pr-12 text-white outline-none transition ${errors.pinCode ? 'border-rose-400 bg-rose-500/10' : 'border-white/5 bg-gray-950/60 focus:border-indigo-400'}`}
                  style={darkAmountInput ? { backgroundColor: 'rgba(5,6,10,0.9)' } : undefined}
                />
                <button
                  type="button"
                  onClick={() => setShowPin(prev => !prev)}
                  className="absolute right-3 top-2.5 rounded-full border border-white/10 bg-white/10 p-2 text-white/70 transition hover:bg-white/20"
                >
                  {showPin ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.pinCode && (
                <p className="mt-2 flex items-center text-sm text-rose-400">
                  <AlertCircle size={15} className="mr-2" />
                  {errors.pinCode}
                </p>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 flex flex-col gap-4 border-t border-white/10 pt-6 lg:flex-row lg:justify-end">
            <button
              type="button"
              onClick={() => {
                resetForm();
                onClose();
              }}
              className="flex-1 rounded-2xl border border-white/15 px-4 py-3 text-center font-semibold text-white/80 transition hover:border-white/40 hover:bg-white/5 lg:flex-none lg:px-8"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={loading}
              className={`flex-1 rounded-2xl px-4 py-3 text-center font-semibold text-white transition lg:flex-none lg:px-8 ${loading ? 'bg-indigo-400/60 cursor-not-allowed' : 'bg-indigo-500 hover:bg-indigo-600'}`}
            >
              {loading ? 'Validation...' : 'Valider le mouvement'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CashMovementModal;
